import Box from "@/ui/atoms/Box";
import type { BoxProps } from "@/ui/atoms/Box";
import DealActivityList from "@/ui/organisms/DealActivity/DealActivityList";
import type { DealActivity } from "@/ui/organisms/DealActivity/DealActivityList";

import { Icon, IconBox } from "@proda-ai/ui-kit";
import Typography from "@/ui/atoms/Typography";

import type {
    ViewFormattedDateProps,
    ViewFormattedDateParams,
} from "@/ui/views/ViewFormattedDate";
import { createViewFormattedDate } from "@/ui/views/ViewFormattedDate";

export const testIds = {
    title: "ViewDealActivities-title",
    backButton: "ViewDealActivities-backButton",
};

export type ViewDealActivitiesParams = {
    useState: (params: { projectId: number; databaseId: number }) => {
        activityList: DealActivity[];
    };
    useFormattedDateState: ViewFormattedDateParams["useState"];
};

export type ViewDealActivitiesProps = {
    projectId: number;
    databaseId: number;
    handleBackButtonClicked: () => void;
} & Omit<BoxProps, "children">;

export const createViewDealActivities = ({
    useState,
    useFormattedDateState,
}: ViewDealActivitiesParams) => {
    const ViewFormattedDate = createViewFormattedDate({
        useState: useFormattedDateState,
    });
    const ComponentFormattedDate = (props: {
        date: ViewFormattedDateProps["date"];
    }) => (
        <ViewFormattedDate
            date={props.date}
            parts={["day", "month", "year", "hour", "minute", "second"]}
        />
    );

    const ViewDealActivities = ({
        projectId,
        databaseId,
        handleBackButtonClicked,
    }: ViewDealActivitiesProps) => {
        const { activityList } = useState({
            projectId,
            databaseId,
        });

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        ml: "24px",
                        mt: "20px",
                        cursor: "pointer",
                        alignItems: "center",
                        gap: "4px",
                        mb: "20px",
                        width: "120px",
                    }}
                    data-testid={testIds.backButton}
                    onClick={handleBackButtonClicked}
                >
                    <IconBox>
                        <Icon icon="chevron-left" size="xs" />
                    </IconBox>
                    <Typography variant="body2">Back to project</Typography>
                </Box>
                <Typography
                    variant="h1"
                    sx={{
                        px: "24px",
                        pb: "16px",
                    }}
                    data-testid={testIds.title}
                >
                    Activity
                </Typography>
                <Box sx={{ flexGrow: 1, px: "32px", mb: "20px" }}>
                    <DealActivityList
                        activityList={activityList}
                        ComponentFormattedDate={ComponentFormattedDate}
                    />
                </Box>
            </Box>
        );
    };

    return ViewDealActivities;
};
